import { Component } from './component.js';
import { ThemeEvents } from './events.js';

/**
 * Delivery Estimate Component
 *
 * Calculates delivery estimates from a customer-entered suburb or postcode.
 *
 * Features:
 * - Zone-aware estimates: metro zones get next-business-day delivery,
 *   Queensland 1-2 business days, other states 1-3 business days
 * - Dispatch countdown respects the block cutoff time (Brisbane/AEST)
 * - Automatically skips weekends and (optionally) public holidays
 * - Typed suburb/postcode entry with autocomplete dropdown
 * - Mobile-first responsive design
 *
 * Important:
 * - All time calculations use Australia/Brisbane timezone (AEST GMT+10)
 * - Queensland doesn't observe daylight saving, so always GMT+10
 * - Cutoff time is based on Brisbane time, not the user's local time
 * - No IP geolocation: the customer always enters their suburb/postcode
 *
 * Usage:
 * Add the 'delivery-estimate' block type to product pages in the theme editor.
 *
 * Configuration:
 * - cutoff_time: Order cutoff time in 24h format (default: "12:00" Brisbane time)
 * - enable_public_holidays: Whether to skip public holidays (default: true)
 * - Styling options available in block settings
 *
 * Maintenance:
 * - Update DISPATCH_HOLIDAYS (the dispatch site's state) and PUBLIC_HOLIDAYS
 *   (destination side) annually, before December, for the following year
 * - When the dispatch site moves (Malpa 3PL), DISPATCH_HOLIDAYS follows its state/area
 * - Verify postcode ranges if delivery zones change
 *
 * @author Xinzuo Development Team
 * @version 4.0.0
 */
class DeliveryEstimateComponent extends Component {
  // Metro delivery postcode ranges (next business day after dispatch)
  static METRO_ZONES = {
    Canberra: [[200, 200], [2600, 2620], [2900, 2914]],
    Sydney: [[1000, 1920], [2000, 2234], [2555, 2574], [2740, 2786]],
    Melbourne: [[3000, 3207], [3340, 3341], [3750, 3750], [3755, 3757], [8001, 8785]],
    Brisbane: [[4000, 4209], [9016, 9464]],
    'Gold Coast': [[2484, 2490], [4210, 4275], [9726, 9726]],
    Adelaide: [[5000, 5174], [5800, 5950]],
    'Perth CBD': [[6000, 6005], [6800, 6892]],
    Tasmania: [
      [7000, 7019],
      [7050, 7053],
      [7055, 7055],
      [7248, 7250],
      [7258, 7258],
      [7275, 7300],
      [7315, 7315],
      [7320, 7320],
    ],
  };

  // Queensland postcode ranges (1-2 business days)
  static QLD_POSTCODES = [[4000, 4999], [9000, 9999]];
  // Dispatch-site holidays: orders are packed and handed to the courier in Wacol, Brisbane (QLD), so no
  // dispatch on Queensland public holidays, including the Brisbane-area Royal Queensland Show day.
  // Source: qld.gov.au/recreation/travel/holidays/public (read 4 Oct 2026). Weekend dates are listed for
  // completeness only. Must follow the 3PL's site once Malpa goes live.
  static DISPATCH_HOLIDAYS = [
    // 2026
    '2026-01-01', // New Year's Day
    '2026-01-26', // Australia Day
    '2026-04-03', // Good Friday
    '2026-04-04', // Day after Good Friday
    '2026-04-06', // Easter Monday
    '2026-05-04', // Labour Day (QLD)
    '2026-08-12', // Royal Queensland Show (Brisbane area)
    '2026-10-05', // King's Birthday (QLD)
    '2026-12-25', // Christmas Day
    '2026-12-26', // Boxing Day (Saturday)
    '2026-12-28', // Boxing Day (additional Monday)
    // 2027
    '2027-01-01', // New Year's Day
    '2027-01-26', // Australia Day
    '2027-03-26', // Good Friday
    '2027-03-27', // Day after Good Friday
    '2027-03-29', // Easter Monday
    '2027-04-26', // Anzac Day (25 April is a Sunday; QLD observes Monday)
    '2027-05-03', // Labour Day (QLD)
    '2027-08-11', // Royal Queensland Show (Brisbane area)
    '2027-10-04', // King's Birthday (QLD)
    '2027-12-27', // Christmas Day (additional Monday)
    '2027-12-28', // Boxing Day (additional Tuesday)
  ];

  // Destination-side holidays (national dates plus the June King's Birthday most states keep): no
  // delivery days. Queensland destinations also skip DISPATCH_HOLIDAYS (see deliveryHolidays).
  // Note: Update this list annually to maintain accurate delivery estimates
  static PUBLIC_HOLIDAYS = [
    // 2025
    '2025-01-01', // New Year's Day
    '2025-01-27', // Australia Day
    '2025-04-18', // Good Friday
    '2025-04-19', // Easter Saturday
    '2025-04-21', // Easter Monday
    '2025-04-25', // Anzac Day
    '2025-06-09', // Queen's Birthday (varies by state)
    '2025-12-25', // Christmas Day
    '2025-12-26', // Boxing Day
    // 2026
    '2026-01-01', // New Year's Day
    '2026-01-26', // Australia Day
    '2026-04-03', // Good Friday
    '2026-04-04', // Easter Saturday
    '2026-04-06', // Easter Monday
    '2026-04-27', // Anzac Day (observed)
    '2026-06-08', // Queen's Birthday
    '2026-12-25', // Christmas Day
    '2026-12-28', // Boxing Day (observed)
    // 2027
    '2027-01-01', // New Year's Day
    '2027-01-26', // Australia Day
    '2027-03-26', // Good Friday
    '2027-03-29', // Easter Monday
    '2027-06-14', // King's Birthday (most states)
    '2027-12-27', // Christmas Day (observed)
    '2027-12-28', // Boxing Day (observed)
  ];

  listen(target, type, handler, options = {}) {
    target.addEventListener(type, handler, { ...options, signal: this.lifecycle.signal });
  }

  frame(callback) {
    const lifecycle = this.lifecycle;
    const id = requestAnimationFrame(() => {
      this.pendingFrames.delete(id);
      if (!lifecycle.signal.aborted && this.isConnected) callback();
    });
    this.pendingFrames.add(id);
    return id;
  }

  delay(callback, ms) {
    const lifecycle = this.lifecycle;
    const id = setTimeout(() => {
      this.pendingTimers.delete(id);
      if (!lifecycle.signal.aborted && this.isConnected) callback();
    }, ms);
    this.pendingTimers.add(id);
    return id;
  }

  connectedCallback() {
    super.connectedCallback();
    this.lifecycle?.abort();
    this.lifecycle = new AbortController();
    this.pendingFrames = new Set();
    this.pendingTimers = new Set();

    this.cutoffTime = this.dataset.cutoffTime || '12:00';
    this.enableHolidays = this.dataset.enableHolidays === 'true';
    this.holidayMode = this.dataset.holidayMode === 'true';
    this.holidayMessage = this.dataset.holidayMessage || '';
    this.postcodeData = null;
    this.postcodeDataLoading = false;
    this.postcodeDataLoaded = false;
    this.selectedIndex = -1;
    this.selectedSuburb = null; // Track selected suburb name

    // Holiday mode: show message and skip all delivery calculation
    if (this.holidayMode && this.holidayMessage) {
      this.frame(() => {
        const destinationText = this.refs.destinationText;
        const countdownBox = this.refs.countdownBox;
        const toggleButton = this.refs.toggleButton;
        if (destinationText) {
          const message = document.createElement('strong');
          message.textContent = `📦 ${this.holidayMessage}`;
          destinationText.replaceChildren(message);
        }
        if (countdownBox) countdownBox.style.display = 'none';
        // Disable the toggle/input — no postcode needed
        if (toggleButton) {
          toggleButton.style.cursor = 'default';
          toggleButton.disabled = true;
          const chevron = toggleButton.querySelector('.delivery-chevron');
          if (chevron) chevron.style.display = 'none';
        }
      });
      return;
    }

    // Set up variant update listener for stock badge
    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      this.listen(closestSection, ThemeEvents.variantUpdate, this.updateStockBadge);
    }

    // Wait for refs to be populated.
    // No geolocation or auto-detection: the card starts in the
    // "Enter suburb or postcode" state until the customer types one.
    this.frame(() => {
      this.postcodeInput = this.refs.postcodeInput;
      this.toggleButton = this.refs.toggleButton;
      this.inputWrapper = this.refs.inputWrapper;
      this.destinationText = this.refs.destinationText;
      this.countdownBox = this.refs.countdownBox;
      this.countdownText = this.refs.countdownText;
      this.errorContainer = this.refs.errorContainer;
      this.errorMessage = this.refs.errorMessage;
      this.dropdown = this.refs.dropdown;
      this.dropdownList = this.refs.dropdownList;
      this.stockBadge = this.refs.stockBadge;

      this.bindEvents();
    });
  }

  disconnectedCallback() {
    this.lifecycle?.abort();
    this.pendingFrames?.forEach(cancelAnimationFrame);
    this.pendingTimers?.forEach(clearTimeout);
    this.observer?.disconnect();
    super.disconnectedCallback();
  }

  bindEvents() {
    if (!this.toggleButton || !this.postcodeInput) return;

    // Handle toggle button click to show/hide input
    this.listen(this.toggleButton, 'click', () => this.toggleInput());

    // Lazy load postcode data on first focus (for autocomplete)
    this.listen(this.postcodeInput, 'focus', () => {
      if (!this.postcodeDataLoaded && !this.postcodeDataLoading) {
        this.loadPostcodeData();
      }
    });

    // Handle input for autocomplete
    this.listen(this.postcodeInput, 'input', (e) => {
      this.handleAutocomplete(e.target.value);
    });

    // Handle keyboard navigation
    this.listen(this.postcodeInput, 'keydown', (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.navigateDropdown(1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.navigateDropdown(-1);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (this.selectedIndex >= 0) {
          this.selectDropdownItem();
        } else {
          this.handleCheck();
        }
      } else if (e.key === 'Escape') {
        this.hideDropdown();
      }
    });

    // Handle blur to hide dropdown
    this.listen(this.postcodeInput, 'blur', () => {
      // Delay to allow click on dropdown item
      this.delay(() => this.hideDropdown(), 200);
    });

    // Close dropdown when clicking outside
    this.listen(document, 'click', (e) => {
      if (!this.contains(e.target)) {
        this.hideDropdown();
      }
    });
  }

  /**
   * Load Australian postcode data from JSON file
   * Lazy loaded on first focus for better PageSpeed performance
   */
  async loadPostcodeData() {
    if (this.postcodeDataLoading || this.postcodeDataLoaded) return;

    this.postcodeDataLoading = true;

    try {
      const postcodeDataUrl = this.dataset.postcodeDataUrl;
      if (!postcodeDataUrl) {
        console.error('Postcode data URL not configured');
        this.postcodeDataLoading = false;
        return;
      }

      const signal = this.lifecycle.signal;
      const response = await fetch(postcodeDataUrl, { signal });
      if (signal.aborted) return;
      if (!response.ok) {
        console.error('Failed to load postcode data');
        this.postcodeDataLoading = false;
        return;
      }
      const data = await response.json();
      if (signal.aborted) return;
      this.postcodeData = data;
      this.postcodeDataLoaded = true;
      this.postcodeDataLoading = false;

      // Enhance existing postcode with suburb if data now available
      this.enhancePostcodeWithSuburb();
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Error loading postcode data:', error);
      this.postcodeDataLoading = false;
    }
  }

  /**
   * Enhance existing postcode input with suburb name after data loads
   */
  enhancePostcodeWithSuburb() {
    if (!this.postcodeData) return;

    // Check if we have a postcode in the input
    const currentValue = this.postcodeInput.value.trim();
    const postcodeMatch = currentValue.match(/\d{4}/);

    if (postcodeMatch && !currentValue.includes(',')) {
      // We have a postcode but no suburb name yet
      const postcode = postcodeMatch[0];
      const match = this.postcodeData.find((item) => item.postcode === postcode);

      if (match) {
        this.postcodeInput.value = `${match.locality}, ${postcode}`;
        this.selectedSuburb = match.locality;
      }
    }
  }

  /**
   * Toggle the input field visibility
   */
  toggleInput() {
    const isExpanded = this.inputWrapper.style.display !== 'none';

    if (isExpanded) {
      // Close input
      this.inputWrapper.style.display = 'none';
      this.toggleButton.classList.remove('expanded');
      this.hideDropdown();
    } else {
      // Open input
      this.inputWrapper.style.display = 'block';
      this.toggleButton.classList.add('expanded');
      this.postcodeInput.focus();

      // Load postcode data if not loaded yet
      if (!this.postcodeDataLoaded && !this.postcodeDataLoading) {
        this.loadPostcodeData();
      }
    }
  }

  /**
   * Handle autocomplete as user types
   */
  handleAutocomplete(query) {
    if (!this.postcodeData) return;

    const trimmedQuery = query.trim().toLowerCase();

    // Hide dropdown if query is too short
    if (trimmedQuery.length < 2) {
      this.hideDropdown();
      return;
    }

    // Filter postcodes - search both postcode and locality
    const matches = this.postcodeData.filter((item) => {
      const postcodeMatch = item.postcode.includes(trimmedQuery);
      const localityMatch = item.locality.toLowerCase().includes(trimmedQuery);
      return postcodeMatch || localityMatch;
    });

    // Limit to first 8 results for performance
    const limitedMatches = matches.slice(0, 8);

    if (limitedMatches.length > 0) {
      this.showDropdown(limitedMatches);
    } else {
      this.hideDropdown();
    }
  }

  /**
   * Display dropdown with filtered results
   */
  showDropdown(matches) {
    this.dropdownList.innerHTML = '';
    this.selectedIndex = -1;

    matches.forEach((item, index) => {
      const li = document.createElement('li');
      const localityText = document.createTextNode(`${item.locality} `);
      const postcodeSpan = document.createElement('span');
      postcodeSpan.className = 'dropdown-postcode';
      postcodeSpan.textContent = item.postcode;
      li.appendChild(localityText);
      li.appendChild(postcodeSpan);
      li.dataset.postcode = item.postcode;
      li.dataset.locality = item.locality;
      li.dataset.index = index;

      this.listen(li, 'click', () => {
        // Set input to "Suburb, Postcode" format
        this.postcodeInput.value = `${item.locality}, ${item.postcode}`;
        this.selectedSuburb = item.locality;
        this.hideDropdown();
        this.handleCheck(); // Auto-calculate on selection
      });

      this.dropdownList.appendChild(li);
    });

    this.dropdown.style.display = 'block';
  }

  /**
   * Hide the dropdown
   */
  hideDropdown() {
    if (this.dropdown) {
      this.dropdown.style.display = 'none';
      this.selectedIndex = -1;
    }
  }

  /**
   * Navigate dropdown with arrow keys
   */
  navigateDropdown(direction) {
    const items = this.dropdownList.querySelectorAll('li');
    if (items.length === 0) return;

    // Remove previous selection
    if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
      items[this.selectedIndex].classList.remove('selected');
    }

    // Update index
    this.selectedIndex += direction;

    // Loop around
    if (this.selectedIndex < 0) {
      this.selectedIndex = items.length - 1;
    } else if (this.selectedIndex >= items.length) {
      this.selectedIndex = 0;
    }

    // Add new selection
    if (items[this.selectedIndex]) {
      items[this.selectedIndex].classList.add('selected');
      items[this.selectedIndex].scrollIntoView({ block: 'nearest' });
    }
  }

  /**
   * Select the currently highlighted dropdown item
   */
  selectDropdownItem() {
    const items = this.dropdownList.querySelectorAll('li');
    if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
      const postcode = items[this.selectedIndex].dataset.postcode;
      const locality = items[this.selectedIndex].dataset.locality;
      // Set input to "Suburb, Postcode" format
      this.postcodeInput.value = `${locality}, ${postcode}`;
      this.selectedSuburb = locality;
      this.hideDropdown();
      this.handleCheck(); // Auto-calculate on selection
    }
  }

  handleCheck() {
    let input = this.postcodeInput.value.trim();

    // Hide previous results
    this.hideResults();
    this.hideDropdown();

    // Validate input
    if (!input) {
      this.showError('Please enter a suburb or postcode');
      return;
    }

    let postcode = null;
    let suburb = null;

    // Check if input is in "Suburb, Postcode" format
    if (input.includes(',')) {
      const parts = input.split(',').map((p) => p.trim());
      if (parts.length === 2) {
        suburb = parts[0];
        postcode = parts[1];
      }
    }
    // If just postcode (numbers only)
    else if (/^\d{4}$/.test(input)) {
      postcode = input;
      // Try to find a suburb for this postcode
      const match = this.postcodeData?.find((item) => item.postcode === postcode);
      if (match) {
        suburb = match.locality;
        // Update input to show suburb too
        this.postcodeInput.value = `${suburb}, ${postcode}`;
        this.selectedSuburb = suburb;
      }
    }
    // If suburb name only
    else {
      const inputLower = input.toLowerCase();
      const match = this.postcodeData?.find(
        (item) => item.locality.toLowerCase() === inputLower
      );

      if (match) {
        postcode = match.postcode;
        suburb = match.locality;
        // Update input to show full format
        this.postcodeInput.value = `${suburb}, ${postcode}`;
        this.selectedSuburb = suburb;
      } else {
        this.showError('Please select a suburb from the dropdown or enter a postcode');
        return;
      }
    }

    // Validate postcode
    if (!postcode || postcode.length !== 4 || !/^\d{4}$/.test(postcode)) {
      this.showError('Please enter a valid 4-digit Australian postcode');
      return;
    }

    const postcodeNum = parseInt(postcode, 10);

    // Check if valid Australian postcode range (0200-9999)
    if (postcodeNum < 200 || postcodeNum > 9999) {
      this.showError('Please enter a valid Australian postcode');
      return;
    }

    // Store suburb for display
    this.selectedSuburb = suburb;

    // Calculate delivery estimate
    const estimate = this.calculateDeliveryEstimate(postcode);
    this.showResult(estimate);
  }

  /**
   * Zone-aware estimate. Dispatch day: today before the cutoff on a business
   * day, otherwise the next business day. Metro zones deliver the next
   * business day after dispatch; Queensland 1-2 business days; other states
   * 1-3 business days.
   */
  calculateDeliveryEstimate(postcode) {
    // Get current time in Brisbane/Queensland timezone (AEST GMT+10)
    const brisbaneTime = new Date(
      new Date().toLocaleString('en-US', { timeZone: 'Australia/Brisbane' })
    );
    const [cutoffHour, cutoffMinute] = this.cutoffTime.split(':').map(Number);

    const timeRemaining = this.getTimeRemaining(brisbaneTime, cutoffHour, cutoffMinute);
    // Dispatch days follow the dispatch site's holidays (QLD); delivery days the destination's.
    const dispatchHolidays = DeliveryEstimateComponent.DISPATCH_HOLIDAYS;
    const dispatchToday = timeRemaining !== null && !this.isWeekendOrHoliday(brisbaneTime, dispatchHolidays);

    const dispatchDate = dispatchToday
      ? new Date(brisbaneTime)
      : this.getNextBusinessDay(brisbaneTime, dispatchHolidays);

    const metroZone = this.findMetroZone(postcode);

    if (metroZone) {
      // Next business day after dispatch
      const deliveryDate = this.addBusinessDays(dispatchDate, 1, this.deliveryHolidays(postcode));
      return {
        type: 'metro',
        zone: metroZone,
        date: deliveryDate,
        dateInfo: this.formatDeliveryDate(deliveryDate),
        timeRemaining,
        dispatchToday,
        postcode,
      };
    }

    return {
      type: 'standard',
      isQLD: this.isQueensland(postcode),
      timeRemaining,
      dispatchToday,
      postcode,
    };
  }

  findMetroZone(postcode) {
    for (const [zone, ranges] of Object.entries(DeliveryEstimateComponent.METRO_ZONES)) {
      for (const [min, max] of ranges) {
        if (postcode >= min && postcode <= max) {
          return zone;
        }
      }
    }
    return null;
  }

  isQueensland(postcode) {
    for (const [min, max] of DeliveryEstimateComponent.QLD_POSTCODES) {
      if (postcode >= min && postcode <= max) {
        return true;
      }
    }
    return false;
  }

  // Holidays that stop a delivery day at the destination: the destination-side list, plus the
  // Queensland list for Queensland postcodes (the same state as the dispatch site).
  deliveryHolidays(postcode) {
    const holidays = DeliveryEstimateComponent.PUBLIC_HOLIDAYS;
    return this.isQueensland(postcode)
      ? holidays.concat(DeliveryEstimateComponent.DISPATCH_HOLIDAYS)
      : holidays;
  }

  getNextBusinessDay(fromDate, holidays) {
    const nextDay = new Date(fromDate);
    nextDay.setDate(nextDay.getDate() + 1);
    while (this.isWeekendOrHoliday(nextDay, holidays)) {
      nextDay.setDate(nextDay.getDate() + 1);
    }
    return nextDay;
  }

  addBusinessDays(fromDate, businessDays, holidays) {
    const result = new Date(fromDate);
    let daysToAdd = businessDays;
    while (daysToAdd > 0) {
      result.setDate(result.getDate() + 1);
      if (!this.isWeekendOrHoliday(result, holidays)) {
        daysToAdd--;
      }
    }
    return result;
  }

  isWeekendOrHoliday(date, holidays = DeliveryEstimateComponent.PUBLIC_HOLIDAYS) {
    // Check weekend (0 = Sunday, 6 = Saturday)
    const day = date.getDay();
    if (day === 0 || day === 6) {
      return true;
    }

    // Check public holidays if enabled
    if (this.enableHolidays) {
      // The date holds Brisbane wall-clock time in the browser's own zone, so its calendar day is read
      // from the local fields. toISOString() (UTC) gave the previous day before 10-11am on the east coast.
      const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      if (holidays.includes(dateString)) {
        return true;
      }
    }

    return false;
  }

  formatDeliveryDate(date) {
    // Get tomorrow's date in Brisbane timezone for comparison
    const brisbaneNow = new Date(
      new Date().toLocaleString('en-US', { timeZone: 'Australia/Brisbane' })
    );
    const tomorrowBrisbane = new Date(brisbaneNow);
    tomorrowBrisbane.setDate(tomorrowBrisbane.getDate() + 1);

    // Compare just the date parts (ignore time)
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const tomorrowOnly = new Date(tomorrowBrisbane.getFullYear(), tomorrowBrisbane.getMonth(), tomorrowBrisbane.getDate());

    // Always format the actual date
    // No timeZone here: the date already carries Brisbane wall-clock time in the browser's own zone, so
    // converting it again moved late-afternoon estimates a day on for shoppers west of Brisbane or overseas.
    const options = {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    };
    const formattedDate = date.toLocaleDateString('en-AU', options);

    // Return object with both tomorrow flag and formatted date
    if (dateOnly.getTime() === tomorrowOnly.getTime()) {
      return {
        isTomorrow: true,
        formatted: formattedDate
      };
    }

    return {
      isTomorrow: false,
      formatted: formattedDate
    };
  }

  getTimeRemaining(now, cutoffHour, cutoffMinute) {
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTime = currentHour * 60 + currentMinute;
    const cutoffTimeMinutes = cutoffHour * 60 + cutoffMinute;

    if (currentTime < cutoffTimeMinutes) {
      const minutesRemaining = cutoffTimeMinutes - currentTime;
      const hoursRemaining = Math.floor(minutesRemaining / 60);
      const minsRemaining = minutesRemaining % 60;

      if (hoursRemaining > 0 && minsRemaining > 0) {
        return `${hoursRemaining}hrs ${minsRemaining}min`;
      } else if (hoursRemaining > 0) {
        return `${hoursRemaining}hrs`;
      } else {
        return `${minsRemaining}min`;
      }
    }
    return null;
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  showResult(estimate) {
    // Format location as "Suburb, Postcode" with underline
    const locationDisplay = this.selectedSuburb
      ? `<u>${this.escapeHtml(this.selectedSuburb)}, ${estimate.postcode}</u>`
      : `<u>${estimate.postcode}</u>`;

    if (estimate.type === 'metro') {
      // Metro zone: next business day after dispatch
      const when = estimate.dateInfo.isTomorrow ? 'Tomorrow' : estimate.dateInfo.formatted;
      this.destinationText.innerHTML = `Earliest delivery <strong>${when}</strong> to ${locationDisplay}`;

      if (estimate.dispatchToday && estimate.timeRemaining && estimate.dateInfo.isTomorrow) {
        this.countdownText.innerHTML =
          `Order in the next <strong>${estimate.timeRemaining}</strong> to get it <strong>tomorrow</strong>`;
        this.countdownBox.style.display = 'flex';
      } else {
        this.countdownBox.style.display = 'none';
      }
    } else {
      // Non-metro: range only, no exact date claims
      const businessDays = estimate.isQLD ? '1-2' : '1-3';
      this.destinationText.innerHTML = `Earliest delivery: ${businessDays} business days to ${locationDisplay}`;

      if (estimate.dispatchToday && estimate.timeRemaining) {
        this.countdownText.innerHTML =
          `Order in the next <strong>${estimate.timeRemaining}</strong> for dispatch <strong>today</strong>`;
        this.countdownBox.style.display = 'flex';
      } else {
        this.countdownBox.style.display = 'none';
      }
    }

    // Close input after calculation
    this.inputWrapper.style.display = 'none';
    this.toggleButton.classList.remove('expanded');

    this.hideError();
  }

  showError(message) {
    this.errorMessage.textContent = message;
    this.errorContainer.style.display = 'block';
  }

  hideError() {
    this.errorContainer.style.display = 'none';
  }

  hideResults() {
    if (this.closest('xz-v2-pdp')) {
      this.destinationText.innerHTML = 'Est. Delivery: <span class="xz-v2-delivery-action">Enter Suburb or Postcode</span>';
    } else {
      this.destinationText.textContent = 'Earliest delivery: Enter suburb or postcode';
    }
    this.countdownBox.style.display = 'none';
    this.hideError();
  }

  /**
   * Updates the stock badge visibility based on variant availability
   * Triggered by variant:update event
   */
  updateStockBadge = (event) => {
    // Ensure this update is for the correct product
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const variant = event.detail.resource;
    // Prefer the fresh Liquid map so changed products/metafield dates are current.
    const rendered = event.detail.data.html?.querySelector(
      `delivery-estimate-component[data-product-id="${this.dataset.productId}"]`
    );
    if (rendered) {
      this.dataset.variantStates = rendered.dataset.variantStates || '';
      this.dataset.variantInventory = rendered.dataset.variantInventory || '';
      this.variantInventory = null;
    }
    const states = Object.fromEntries((this.dataset.variantStates || '').split(',')
      .filter(Boolean).map(pair => pair.split(':')));
    const state = variant ? states[String(variant.id)] || 'unavailable' : 'unavailable';
    this.dataset.purchaseState = state;
    this.hidden = state !== 'buyable';
    if (!this.stockBadge) return;
    const available = state === 'buyable';
    this.stockBadge.classList.toggle('stock-badge--hidden', !available);
    this.stockBadge.dataset.available = String(available);
    if (available) this.applyLowStockState(variant.id);
  };

  /**
   * Swaps the stock badge into its low-stock state when the selected variant
   * has 1-9 units left. Inventory comes from data-variant-inventory ("id:qty"
   * pairs rendered by delivery-estimate-calculator.liquid); qty -1 marks
   * untracked / oversell-allowed variants, which never show low stock.
   */
  applyLowStockState(variantId) {
    const textEl = this.refs.stockBadgeText;
    if (!textEl) return;

    if (!this.variantInventory) {
      this.variantInventory = {};
      for (const pair of (this.dataset.variantInventory || '').split(',')) {
        const [id, qty] = pair.split(':');
        if (id) this.variantInventory[id.trim()] = Number(qty);
      }
    }

    const qty = this.variantInventory[String(variantId)];
    const isLow = Number.isFinite(qty) && qty > 0 && qty < 10;
    this.stockBadge.classList.toggle('stock-badge--low', isLow);
    if (isLow) {
      textEl.textContent = qty <= 5 ? `Only ${qty} left` : 'Low stock';
    } else {
      textEl.textContent = textEl.dataset.defaultText || 'In stock';
    }
  }
}

customElements.define('delivery-estimate-component', DeliveryEstimateComponent);
